import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import type { Option } from '@/constants/institute';
import { floatingHostFor, promoteToTopLayer, type FloatingHost } from '@/lib/topLayer';
import { cn } from '@/lib/utils';
import { FieldNote } from './FieldNote';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: ReactNode;
  labelClassName?: string;
  options: readonly Option[];
  error?: string;
  hint?: ReactNode;
  /** Shown as muted text until something is chosen — omit for a field with a real default. */
  placeholder?: string;
  /**
   * Keeps the label for screen readers but takes it off the screen — for a
   * filter bar that already says what is being filtered. The label must still
   * name the field ("Show marked or unmarked"), never just "Show". Mirrors
   * `Input`'s and `Checkbox`'s prop of the same name.
   */
  hideLabel?: boolean;
  /** Disables the search box inside the listbox dropdown panel. */
  hideSearch?: boolean;
  /** Explicit width for the dropdown panel. */
  minPanelWidth?: number;
  required?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    labelClassName,
    options,
    error,
    hint,
    placeholder,
    hideLabel = false,
    hideSearch = false,
    minPanelWidth,
    required = false,
    className,
    id,
    value,
    defaultValue,
    disabled,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const labelId = `${fieldId}-label`;
  const listboxId = `${fieldId}-listbox`;
  const optionId = (index: number) => `${fieldId}-option-${index}`;

  const selectRef = useRef<HTMLSelectElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  /** Where the panel is drawn, which a modal on the screen changes. */
  const [host, setHost] = useState<FloatingHost>(() => ({
    container: document.body,
    needsTopLayer: false,
  }));
  const [opensUpward, setOpensUpward] = useState(false);
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState('');

  const isControlled = value !== undefined;

  // The hidden select is the source of truth when uncontrolled: react-hook-form
  // writes straight to the DOM node on mount and on `reset()`, without firing an
  // event, so mirroring it after each render is what keeps the button honest.
  const [domValue, setDomValue] = useState(() => String(defaultValue ?? ''));
  useEffect(() => {
    const node = selectRef.current;
    if (!node || isControlled) return;
    if (node.value !== domValue) setDomValue(node.value);
  });

  const selectedValue = isControlled ? String(value ?? '') : domValue;
  const selectedOption = options.find((option) => option.value === selectedValue);

  const visibleOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  /** Keeps our handle on the panel, and lifts it clear of an open dialog. */
  const attachPanel = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (host.needsTopLayer) promoteToTopLayer(node);
    },
    [host.needsTopLayer],
  );

  /** Forwards the node to the caller's ref while keeping our own handle on it. */
  const attachSelect = useCallback(
    (node: HTMLSelectElement | null) => {
      selectRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  function openPanel() {
    if (disabled) return;
    // Opens on the current choice, so arrowing starts from where the user is.
    const selectedIndex = options.findIndex((option) => option.value === selectedValue);
    setQuery('');
    setActiveIndex(Math.max(selectedIndex, 0));
    // Asked each time rather than once: the same field is drawn on a page and
    // inside a dialog, and only the second needs the top layer.
    setHost(floatingHostFor(triggerRef.current));
    setIsOpen(true);
  }

  function closePanel() {
    setIsOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }

  function commit(nextValue: string) {
    const node = selectRef.current;
    closePanel();
    if (!node || nextValue === selectedValue) return;

    node.value = nextValue;
    setDomValue(nextValue);
    node.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * The hidden select never receives focus, so its `blur` would never fire —
   * and forms validating on blur would never validate a dropdown. Firing
   * `focusout` on it when focus genuinely leaves the field restores that.
   */
  function handleFieldBlur(event: ReactFocusEvent<HTMLDivElement>) {
    // Our own synthetic blur bubbles back through here; ignore it or it loops.
    // Read the native target — React types `target` as the handler's element,
    // which is only true for events that did not bubble.
    if (event.nativeEvent.target === selectRef.current) return;

    const nextFocused = event.relatedTarget;
    // Focus stays inside the field's visible widget (button, filter input, list)
    if (nextFocused instanceof Node && event.currentTarget.contains(nextFocused)) return;
    // Focus moved into the portal panel (which lives at document.body in the DOM
    // but is logically part of this field — e.g. the auto-focused search input).
    if (nextFocused instanceof Node && panelRef.current?.contains(nextFocused)) return;

    setIsOpen(false);
    setQuery('');
    selectRef.current?.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
  }

  // --- keyboard -------------------------------------------------------------

  function moveActive(delta: number) {
    if (visibleOptions.length === 0) return;
    setActiveIndex((current) => {
      const next = current + delta;
      if (next < 0) return visibleOptions.length - 1;
      if (next >= visibleOptions.length) return 0;
      return next;
    });
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (disabled) return;

    if (!isOpen) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        openPanel();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(Math.max(visibleOptions.length - 1, 0));
        return;
      case 'Enter': {
        event.preventDefault();
        const option = visibleOptions[activeIndex];
        if (option) commit(option.value);
        return;
      }
      case 'Escape':
        event.preventDefault();
        closePanel();
        return;
      case 'Tab':
        // Let focus leave naturally, but don't leave the panel hanging open.
        setIsOpen(false);
        setQuery('');
        return;
      // Everything else — space included — is left to the filter box.
    }
  }

  // --- panel placement and focus -------------------------------------------

  const showSearch = !hideSearch && options.length > 5;

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current;
    if (!trigger) return;

    const box = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - box.bottom;
    const spaceAbove = box.top;
    const opensUp = spaceBelow < 260 && spaceAbove > spaceBelow;
    setOpensUpward(opensUp);

    const calculatedWidth = minPanelWidth ?? Math.max(box.width, showSearch ? 220 : 200);
    const adjustedLeft = Math.max(8, Math.min(box.left, window.innerWidth - calculatedWidth - 8));

    setPanelStyle({
      top: opensUp ? box.top : box.bottom + 4,
      left: adjustedLeft,
      width: box.width,
    });
  }, [isOpen, minPanelWidth, showSearch]);

  // Close when user scrolls outside so the panel doesn't float away from the trigger
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event) => {
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };
    const handleResize = () => setIsOpen(false);

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && showSearch) searchRef.current?.focus();
  }, [isOpen, showSearch]);

  useEffect(() => {
    if (!isOpen) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, activeIndex]);

  // --- render ---------------------------------------------------------------

  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} id={labelId} className={cn('lbl', labelClassName, hideLabel && 'sr-only')}>
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </label>

      <div className="relative" onKeyDown={handleKeyDown} onBlur={handleFieldBlur}>
        <select
          ref={attachSelect}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          // Present for the form, absent for the user: the button below is the
          // control people see, focus and operate.
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none pb-0 absolute h-0 w-0 opacity-0"
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          ref={triggerRef}
          type="button"
          id={fieldId}
          role="combobox"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-labelledby={labelId}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          onClick={() => (isOpen ? closePanel() : openPanel())}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-field border bg-surface px-3 text-left text-sm text-ink py-2',
            'transition-colors duration-150',
            'focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:border-slate-600 dark:focus:ring-slate-700',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-3',
            // Dimmed as well: a page that styles its filters with `!bg-…` and
            // `!text-…` overrides the two above, and a disabled control that
            // still looks live reads as broken rather than unavailable.
            'disabled:opacity-60 disabled:hover:border-line-strong',
            error ? 'border-crit focus:border-crit focus:ring-crit/25' : 'border-line-strong',
            isOpen && !error && 'border-slate-400 ring-1 ring-slate-300/60 dark:border-slate-600 dark:ring-slate-700/60',
            className,
          )}
        >
          <span
            className="min-w-0 flex-1 truncate text-sm"
            style={{ color: selectedOption ? 'var(--text)' : 'var(--text-3)' }}
          >
            {selectedOption?.label ?? placeholder ?? ''}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-ink-3 transition-transform duration-150',
              isOpen && 'rotate-180',
            )}
            aria-hidden
          />
        </button>

        {isOpen && panelStyle && createPortal(
          <div
            ref={attachPanel}
            style={{
              position: 'fixed',
              top: opensUpward ? undefined : panelStyle.top,
              bottom: opensUpward ? window.innerHeight - (panelStyle.top) : undefined,
              left: panelStyle.left,
              minWidth: minPanelWidth ?? Math.max(panelStyle.width, showSearch ? 220 : 200),
              maxWidth: 'calc(100vw - 1rem)',
              zIndex: 9999,
            }}
            className={cn(
              'overflow-hidden rounded-card border border-line bg-surface shadow-card',
              'animate-pop-in',
            )}
            onMouseDown={(event) => {
              // Allow normal focus behavior if clicking inside an input
              if (
                event.target instanceof HTMLInputElement ||
                (event.target as HTMLElement).closest('input')
              ) {
                return;
              }
              event.preventDefault();
            }}
          >
            {showSearch && (
              <div className="relative border-b border-line">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3"
                  aria-hidden
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActiveIndex(0);
                  }}
                  placeholder="Type to filter"
                  aria-label={typeof label === 'string' ? `Filter ${label.toLowerCase()}` : 'Filter options'}
                  aria-controls={listboxId}
                  aria-activedescendant={
                    visibleOptions.length > 0 ? optionId(activeIndex) : undefined
                  }
                  autoComplete="off"
                  className="h-9 w-full bg-transparent pl-9 pr-3 text-sm font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
                />
              </div>
            )}

            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-labelledby={labelId}
              // Keeps focus on the trigger (or the filter box) while clicking a
              // row, so the field never blurs itself before the click lands.
              onMouseDown={(event) => event.preventDefault()}
              // Tall enough for nine rows: at seven, a list one longer cut its
              // last choice in half and read as if it were missing.
              className="max-h-80 overflow-y-auto overscroll-contain p-1 m-0"
            >
              {visibleOptions.map((option, index) => {
                const isSelected = option.value === selectedValue;
                const isActive = index === activeIndex;

                return (
                  <li
                    key={option.value}
                    id={optionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    data-active={isActive}
                    onClick={() => commit(option.value)}
                    onMouseMove={() => setActiveIndex(index)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors',
                      isActive && 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-semibold',
                      isSelected && 'font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50/50 dark:bg-indigo-950/40',
                    )}
                  >
                    <span className="min-w-0 flex-1 whitespace-nowrap">{option.label}</span>
                  </li>
                );
              })}

              {visibleOptions.length === 0 && (
                <li role="presentation" className="px-3 py-6 text-center text-sm text-ink-3">
                  {query ? `No match for “${query}”` : 'Nothing to choose from'}
                </li>
              )}
            </ul>
          </div>
        , host.container)}
      </div>

      <FieldNote error={error} hint={hint} errorId={errorId} hintId={hintId} />
    </div>
  );
});
